import { useState } from 'react';

import { ArrowsCounterClockwise, ThumbsDown, ThumbsUp } from 'phosphor-react';

import Button from 'components/shared/Button';
import Icon from 'components/shared/Icon';
import Tooltip, { VARIANTS } from 'components/shared/Tooltip';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { showErrorSnackbar } from 'hooks/useSnackbar';
import { makeId } from 'utils/common';

import s from './index.module.css';

type Props = {
  onThumbsUp: () => Promise<unknown>;
  onThumbsDown: () => Promise<unknown>;
  onTryAgain: () => void;
  loading?: boolean;
};

type Feedback = 'positive' | 'negative';

const buttonTooltipIds = [makeId(5), makeId(5), makeId(5)];

export default function AIFeedbackBar(props: Props) {
  const { onThumbsUp, onThumbsDown, onTryAgain, loading } = props;

  const { isMiddleScreen } = useMediaBreakpoints();

  const [rate, setRate] = useState<Feedback>();

  const handleThumbsUp = async () => {
    setRate('positive');
    try {
      await onThumbsUp();
    } catch (e: unknown) {
      setRate(undefined);

      const error = e as { errors: { title: string } };
      if ('errors' in error) {
        showErrorSnackbar(error.errors.title);
        return;
      }

      console.log(e);
    }
  };

  const handleThumbsDown = async () => {
    setRate('negative');
    try {
      await onThumbsDown();
    } catch (e: unknown) {
      setRate(undefined);

      const backendError = e as { errors: { title: string } };
      if ('errors' in backendError) {
        showErrorSnackbar(backendError.errors.title);
        return;
      }

      console.log(e);
    }
  };

  return (
    <div className={s.feedbackBar} tabIndex={0}>
      <Button
        onClick={handleThumbsUp}
        shape="circle"
        size="small"
        type={rate === 'positive' ? 'dark-grey' : 'text'}
        disabled={loading}
        className={s.btn}
        data-tip
        data-for={buttonTooltipIds[0]}
      >
        <ThumbsUp width={18} height={18} />
      </Button>
      <Tooltip id={buttonTooltipIds[0]} variant={VARIANTS.DARK} place={isMiddleScreen ? 'right' : 'top'}>
        Good Response
      </Tooltip>

      <Button
        onClick={handleThumbsDown}
        shape="circle"
        size="small"
        type={rate === 'negative' ? 'dark-grey' : 'text'}
        disabled={loading}
        className={s.btn}
        data-tip
        data-for={buttonTooltipIds[1]}
      >
        <ThumbsDown width={18} height={18} />
      </Button>
      <Tooltip id={buttonTooltipIds[1]} variant={VARIANTS.DARK} place={isMiddleScreen ? 'right' : 'top'}>
        Bad Response
      </Tooltip>

      <Button
        onClick={onTryAgain}
        shape="circle"
        size="small"
        type="text"
        disabled={loading}
        className={s.btn}
        data-tip
        data-for={buttonTooltipIds[2]}
      >
        <Icon
          spin={loading}
          spinDirection="counterclockwise"
          component={() => <ArrowsCounterClockwise width={18} height={18} />}
        />
      </Button>
      <Tooltip id={buttonTooltipIds[2]} variant={VARIANTS.DARK} place={isMiddleScreen ? 'right' : 'top'}>
        Try Again
      </Tooltip>
    </div>
  );
}
